import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { brandIconUrl, resolveAdvertiserDomain } from '@/shared/utils/brandDomains';

interface BrandLogoProps {
  brandName: string;
  website?: string | null;
  size?: number;
}

// No competitor list reaches here, so the name guess does the work. The map stays in the
// signature because it is the tier that carries a real domain once one is available.
const NO_COMPETITOR_DOMAINS = new Map<string, string>();

// Prefers the real (LLM-provided) website; falls back to the shared name resolver, so this
// and the report's own BrandIdentity guess the same domain for the same brand.
function resolveDomain(website?: string | null, brandName?: string): string | null {
  if (website) {
    try {
      const url = website.startsWith('http') ? website : `https://${website}`;
      const hostname = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
      if (hostname) return hostname;
    } catch {
      // fall through to brand-name guess
    }
  }
  return brandName ? resolveAdvertiserDomain(brandName, NO_COMPETITOR_DOMAINS) : null;
}

export function BrandLogo({ brandName, website, size = 24 }: BrandLogoProps) {
  const domain = useMemo(() => resolveDomain(website, brandName), [website, brandName]);
  const iconUrl = useMemo(() => (domain ? brandIconUrl(domain) : null), [domain]);

  // Key by iconUrl so the inner component (which owns the `failed` state) remounts
  // on URL change and starts fresh. Replaces a `useEffect(() => setFailed(false), [iconUrl])`
  // reset (see no-use-effect skill, Rule 5).
  return (
    <BrandLogoInner
      key={iconUrl ?? '__no_icon__'}
      brandName={brandName}
      iconUrl={iconUrl}
      size={size}
    />
  );
}

interface BrandLogoInnerProps {
  brandName: string;
  iconUrl: string | null;
  size: number;
}

function BrandLogoInner({ brandName, iconUrl, size }: BrandLogoInnerProps) {
  const { t } = useTranslation('common');
  const [failed, setFailed] = useState(false);

  const handleError = useCallback(() => setFailed(true), []);

  const initial = brandName.trim().charAt(0).toUpperCase();

  const containerClassName =
    'flex shrink-0 items-center justify-center overflow-hidden rounded-sm border border-border-light';

  if (!iconUrl || failed) {
    return (
      <div className={`${containerClassName} bg-muted`} style={{ width: size, height: size }}>
        <span className="font-bold text-muted-foreground" style={{ fontSize: size * 0.45 }}>
          {initial}
        </span>
      </div>
    );
  }

  return (
    <div className={containerClassName} style={{ width: size, height: size }}>
      <img
        src={iconUrl}
        alt={t('components.brandLogo.alt', { brandName })}
        onError={handleError}
        referrerPolicy="no-referrer"
        className="size-full object-cover"
      />
    </div>
  );
}
